'use client';

import React, { createContext, useContext, useState, ReactNode } from 'react';
import { useSubscriptionStore } from '@/store/subscriptionStore';
import { isPastGrace } from '@/lib/subscription/subscription-timing';
import { getTenantIdFromSession } from '@/lib/tenant/session';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useLocations } from '@/hooks/queries/location';
import { isDemoAccount } from '@/lib/tenant/session';
import { appConfig } from '@/config/app';

export type SubscriptionDialogContextType = {
  interceptAction: (action: () => void) => void;
};

const SubscriptionDialogContext = createContext<SubscriptionDialogContextType | undefined>(undefined);

export const useSubscriptionDialog = () => {
  const context = useContext(SubscriptionDialogContext);
  if (!context) throw new Error('Subscription Dialog must be used within Dialog Provider');
  return context;
};

export const SubscriptionDialogProvider = ({ children }: { children: ReactNode }) => {
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { data: locations } = useLocations();

  const subscription = useSubscriptionStore((s) => s.subscription);
  const subscriptionLoading = useSubscriptionStore(
    (s) => s.subscriptionLoading,
  );
  const plans = useSubscriptionStore((s) => s.plans);

  const isExpired = () => {
    if (subscriptionLoading) return false;
    if (!subscription) return false; // No subscription record = new tenant, allow all actions
    return isPastGrace(subscription, Date.now());
  };

const interceptAction = (action: () => void) => {
  if (isDemoAccount()) {
    action();
    return;
  }
    if (isExpired()) {
      setVisible(true);
    } else {
      action();
    }
 };

  const handleRenew = async () => {
    if (!subscription) {
      setVisible(false);
      return;
    }

    const tenantId = getTenantIdFromSession();
    if (!tenantId) return;

     const quantity = locations ? locations.length : 1;

    setLoading(true);
    setError(null);
    try {
      const matchingPlan = plans.find((p) => p.id === subscription.plan_id);
      const matchingPrice =
        matchingPlan?.prices?.find((p) => p.id === subscription.plan_price_id) ??
        matchingPlan?.prices?.[0];

      if (!matchingPlan || !matchingPrice) throw new Error('Active plan not found');

      const res = await fetch(`${appConfig.appUrl}/api/billing/checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tenant_id: tenantId,
          plan_price_id: matchingPrice.id,
          plan_id: matchingPlan.id,
          interval: 'month',
          add_setup_fees: false,
          return_url: `${appConfig.appUrl}/billing/success`,
          quantity: quantity,
        }),
      });

      const data = await res.json();
      if (data.success && data.url) {
        window.open(data.url, '_blank');
        setVisible(false);
      } else {
        throw new Error(data.error ?? 'Failed to create checkout');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SubscriptionDialogContext.Provider value={{ interceptAction }}>
      {children}

      <Dialog open={visible} onOpenChange={setVisible}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader className="flex flex-col items-center text-center">
            <DialogTitle className="text-xl font-bold">Subscription Expired</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground mt-2">
              Please renew your subscription to perform this action and continue using all features of Nxcut.
            </DialogDescription>
          </DialogHeader>

          {error && <p className="text-xs text-destructive text-center mt-2">{error}</p>}

          <DialogFooter className="flex sm:justify-between gap-2 mt-4">
            <Button variant="outline" onClick={() => setVisible(false)} className="flex-1">
              Dismiss
            </Button>
            <Button onClick={handleRenew} disabled={loading} className="flex-1 bg-blue-600 hover:bg-blue-700 text-white">
              {loading ? 'Redirecting...' : 'Renew Now'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SubscriptionDialogContext.Provider>
  );
};
